import WarpElement from "@warp-ds/elements-core";
import { css, html } from "lit";
import type { NewCarFeaturedModelItem } from "../../models/newCarSections.js";
import { trackClickEvent } from "../tracking.js";

/**
 * "Utvalda modeller" — the curated featured-model grid of the Blocket New Car tab.
 * Data is opaque pass-through from mb-new-car-api (Blocket-only, Swedish); `displayPrice` is
 * pre-formatted by the backend. Each card links to the model page (href computed server-side in
 * renderPage; omitted when the make can't be resolved). Mirrors the mb-new-car-frontend ModelCard
 * stretched-anchor pattern: an empty link covers the card, named from the heading via `aria-owns`.
 */
class NewCarFeaturedModels extends WarpElement {
    declare header: string;
    declare items: NewCarFeaturedModelItem[];

    static properties = {
        header: { type: String },
        items: { type: Array },
    };

    static styles = [
        WarpElement.styles,
        css`
            :host {
                display: block;
            }
            @css-placeholder-nsSmp1;
        `,
    ];

    constructor() {
        super();
        this.header = "";
        this.items = [];
    }

    render() {
        return html`<div class="grid gap-16 max-w-full">
            <h3 class="t3 mb-0">${this.header}</h3>
            <div class="grid gap-16 grid-cols-1 md:grid-cols-3">
                ${this.items.map((item) => {
                    // Warp <w-card> provides the card chrome (surface, border, shadow, hover)
                    // for free. The grid wrapper stretches every card to its row's height, and
                    // h-full + grid-rows-1 make the card fill that height so all cards in a row
                    // are equal — mirrors the mb-new-car-frontend ModelCard.
                    //
                    // grid-rows-1 (a single `minmax(0,1fr)` row) is the load-bearing bit: under
                    // this app's Lit SSR, <w-card> hydrates with a *duplicate* (empty) shadow
                    // surface div alongside the real one. A plain auto-row grid would give that
                    // empty duplicate its own track and leave the real surface content-height
                    // (so short cards wouldn't fill). Pinning a single 1fr row makes the real
                    // surface fill and collapses the empty duplicate to 0.
                    //
                    // article h-full fills the surface so the stretched anchor (absolute,
                    // covers the whole card) carries the link + focus ring over the full card,
                    // named from the heading via aria-owns.
                    return html`<w-card class="relative grid grid-rows-1 h-full">
                        <article class="flex flex-col gap-16 p-16 h-full">
                            <div class="flex flex-col gap-4">
                                <h4 class="font-bold s-text">
                                    ${
                                        item.href
                                            ? html`<a
                                                  data-card-action
                                                  href="${item.href}"
                                                  @click="${() => item.trackingEvent && trackClickEvent(item.trackingEvent)}"
                                              >
                                                  ${item.name}
                                              </a>`
                                            : item.name
                                    }
                                </h4>
                                <span class="s-text-subtle">${item.displayPrice}</span>
                            </div>
                            <!-- width/height are the intrinsic render size from the image contract
                                 (cars render 800×500 = 8:5) so the browser reserves the box and the
                                 image scales to the card width instead of a fixed thumbnail height.
                                 Mirrors the mb-new-car-frontend ModelCard. -->
                            <img
                                class="object-cover pb-8 mx-auto w-91/100"
                                width="800"
                                height="500"
                                loading="lazy"
                                src="${item.image}"
                                alt="${item.name}"
                            />
                            <!-- All spec headings + values come from the API (highlightedSpecifications,
                                 e.g. "Drivmedel"/"Elektrisk", "Räckvidd (WLTP)"/"440 - 570 km"); the
                                 frontend just renders the pairs. -->
                            <div class="flex gap-16">
                                ${item.highlightedSpecifications?.map(
                                    (spec) =>
                                        html`<div class="flex flex-col">
                                            <span class="text-s s-text-subtle">${spec.label}</span>
                                            <span class="font-bold s-text">${spec.value}</span>
                                        </div>`,
                                )}
                            </div>
                        </article>
                    </w-card>`;
                })}
            </div>
        </div>`;
    }
}

customElements.define("new-car-featured-models", NewCarFeaturedModels as unknown as CustomElementConstructor);
